import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, ButtonGroup, Dropdown, DropdownButton, FormControl, InputGroup, Modal, Overlay, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { IoPause } from 'react-icons/io5';
import { PuzzleMetadataKey } from '../../libs/directionsLib';
import { PlayPreferenceKey, PlayStatus, useGroupPlaySessions, usePlayPreferences, usePlayPuzzleData, usePlayState, usePlayStatus } from './PlayInteractionContext';
import { useAppContext } from '../../App';
import { FaRegArrowAltCircleRight, FaVoteYea } from 'react-icons/fa';
import { IoMdRefresh } from 'react-icons/io';
import { possiblyPluralString } from '../../libs/miscLib';
import { getPrintablePDF, getPuzFile } from '../../libs/exportLib';
import { createNewGroupPlaySession } from '../../libs/groupPlaySessionLib';
import { useToastNotifications } from '../../libs/toastLib';
import { useNavigate } from 'react-router-dom';
import { HiOutlineX } from 'react-icons/hi';
import { FaArrowRight, FaCheck, FaCopy, FaPlantWilt, FaRegCopy } from 'react-icons/fa6';
import PlayToolbarAvatar from './PlayToolbarAvatar';
import LoaderButton from '../reusable/LoaderButton';
import { get } from 'aws-amplify/api';



export const DISPLAY_NAMES = [
  'Phil the Fisherman',
  'Timmy the Tyrannosaur',
  'Ashton the Astonisher',
  'Sammy the Sailor',
  'Gary the Gardener',
  'Wally the Wordsmith',
];
export const TODAYS_NAME = DISPLAY_NAMES[Math.floor(Math.random() * DISPLAY_NAMES.length)];

export const CURSOR_COLORS = [
  "#FF5733", // Bright Orange-Red
  "#FFBD33", // Vibrant Yellow-Orange
  "#75FF33", // Lime Green
  "#33FFBD", // Aqua
  "#3375FF", // Bright Blue
  "#8D33FF", // Vivid Purple
  "#FF33A8", // Hot Pink
  "#FF3333"  // Bright Red
];



function msToDisplayTime(ms) {
  const secs = ms / 1000;
  const totalSeconds = Math.floor(Math.max(0, secs - 0.5));
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds - 3600*hours) / 60);
  const seconds = totalSeconds % 60;
  return hours > 99 ? '99h+' : hours > 0 ? `${hours}:${('0' + mins).slice(-2)}:${('0' + seconds).slice(-2)}` : `${mins}:${('0' + seconds).slice(-2)}`;
}



function ShareLinkButton() {
  const [showTooltip, setShowTooltip] = useState(false);
  const target = useRef(null);

  // Full URL to copy
  const fullUrl = window.location.href;

  // Display version: strip protocol + www.
  const displayUrl = fullUrl
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 3000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="small d-flex gap-2 align-items-center">
      <div ref={target}>
        <Button
          className="zoomable-subtle d-flex gap-2 align-items-center rounded px-3 py-0"
          variant="outline-secondary"
          size="sm"
          onClick={handleCopy}
        >
          {displayUrl}
          <FaRegCopy />
        </Button>
      </div>
      <Overlay target={target.current} show={showTooltip} placement="top">
        {(props) => (
          <Tooltip id="copy-tooltip" {...props}>
            Copied!
          </Tooltip>
        )}
      </Overlay>
    </div>
  );
}




export default function PlayToolbar() {

  const { windowWidth, brandIsShowing } = useAppContext();
  const { postErrorNotification } = useToastNotifications();
  const navigate = useNavigate();

  const { getPlayPreference, setPlayPreference } = usePlayPreferences();
  const { gridIsFullAndCorrect, gridIsFull, incorrectLocs, emptyLocs, revealLocs, revealedLocs, displayRebusDialog } = usePlayState();
  const { playStatus, play, pause, reset, getCurrentPlayTime } = usePlayStatus();
  const { puzzleId, puzzleMetadata, charGrid, furnishings, clues } = usePlayPuzzleData();
  const { groupPlaySessionId, connectToSession, liveConnectionInfo, myCursorColor, setMyCursorColor } = useGroupPlaySessions();
  const title = puzzleMetadata.get(PuzzleMetadataKey.TITLE);
  const author = puzzleMetadata.get(PuzzleMetadataKey.AUTHOR);
  const date = puzzleMetadata.get(PuzzleMetadataKey.DATE);
  const editor = puzzleMetadata.get(PuzzleMetadataKey.EDITOR);
  const publisher = puzzleMetadata.get(PuzzleMetadataKey.PUBLISHER);
  const copyright = puzzleMetadata.get(PuzzleMetadataKey.COPYRIGHT);


  // Start & pause modals just display as a function of the playStatus, but the wrong & end modals need to have their own state
  const [showWrongModal, setShowWrongModal] = useState(false);
  const [revealNumberOfWrongLetters, setRevealNumberOfWrongLetters] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);

  // Display the game timer
  const [displayTime, setDisplayTime] = useState('0:00');
  const gameTimerInterval = useRef(null);
  
  // State values for the connecting to group play session modal
  const [isJoining, setIsJoining] = useState(false);
  const [displayNameInput, setDisplayNameInput] = useState('');


  useEffect(() => {
    if (playStatus === PlayStatus.PLAY) {
      // It just turned to PLAY
      if (gameTimerInterval.current) clearInterval(gameTimerInterval.current);
      gameTimerInterval.current = setInterval(() => setDisplayTime(msToDisplayTime(getCurrentPlayTime())), 1000);
    } else if (playStatus === PlayStatus.END) {
      // It just turned to END
      setDisplayTime(msToDisplayTime(getCurrentPlayTime()));
      setShowEndModal(true);
    }

    return () => {
      if (gameTimerInterval.current) clearInterval(gameTimerInterval.current);
    }
  }, [playStatus, getCurrentPlayTime]);

  useEffect(() => {
    if (gridIsFull && !gridIsFullAndCorrect) {
      setShowWrongModal(true);
    }
  }, [gridIsFull, gridIsFullAndCorrect]);



  // Show this modal if the user is supposed to be in a group play session, but hasn't connected yet
  const showJoinGroupPlaySessionModal = !!(groupPlaySessionId && !liveConnectionInfo)
  const [groupSessionWasAlreadyCompleted, setGroupSessionWasAlreadyCompleted] = useState(false);
  const joinGroupPlaySessionModal = (
    <Modal show={showJoinGroupPlaySessionModal} onShow={async () => {
      setIsJoining(false);
      setGroupSessionWasAlreadyCompleted(false);

      // In rare instances, the user will be joining a COMPLETED game - check this in the background
      const groupSessionToJoin = (await (await get({
        apiName: 'groupPlaySessions',
        path: `/${groupPlaySessionId}`,
      }).response).body.json());
      if (groupSessionToJoin.playStatus === PlayStatus.END) {
        setGroupSessionWasAlreadyCompleted(true);
      }
    }}>
      <Modal.Header className='teal-3-bkgd justify-content-center'>
        <strong>Connect to group <span className='fs-5'>{groupPlaySessionId}</span></strong>
      </Modal.Header>
      <Modal.Body className='text-center d-flex flex-column gap-2'>
        {groupSessionWasAlreadyCompleted ? (<div className='d-flex flex-column gap-4 align-items-center'>
          <div className='fw-bold'>This group session has already been solved!</div>
          <FaPlantWilt className='text-success' size={40} />
          <div className='text-muted small balanced-text fst-italic'>
            To start a new group play session, first leave this group, then click the "Start over" button in the toolbar.
          </div>
        </div>) : (<>
          <div className='fw-bold'>Enter your display name</div>
          <FormControl
            className='text-center'
            autoFocus
            value={displayNameInput}
            onChange={e => setDisplayNameInput(e.target.value.slice(0, 27))}
            placeholder={TODAYS_NAME}
            disabled={isJoining}
          />

          <div className='fw-bold mt-3'>Choose your color</div>
          <div className='d-flex gap-3 justify-content-center'>
            {CURSOR_COLORS.map(color => (
              <PlayToolbarAvatar
                className='clickable zoomable'
                key={color}
                color={color}
                letter={displayNameInput.charAt(0)}
                size={30}
                backgroundOpacity={myCursorColor === color ? 0.3 : 0}
                onClick={() => {
                  if (!isJoining) {
                    setMyCursorColor(color);
                  }
                }}
                style={{
                  transform: myCursorColor === color ? 'scale(1.24)' : null,
                }}
              />
            ))}
          </div>

          <div className='mt-5 text-secondary small'>
            You can add others to this game next.
          </div>

          <LoaderButton
            className='fw-bold mx-auto px-5 d-flex align-items-center justify-content-center gap-2 zoomable-subtle'
            variant='outline-primary'
            disabled={!displayNameInput || !myCursorColor}
            isLoading={isJoining}
            onClick={async () => {
              setIsJoining(true);
              await connectToSession({ displayName: displayNameInput, cursorColor: myCursorColor });
            }}
          >
            Join as
            <PlayToolbarAvatar
              color={myCursorColor}
              letter={displayNameInput.charAt(0)}
              size={24}
            />
            {displayNameInput}
            <FaRegArrowAltCircleRight />
          </LoaderButton>
        </>)}

        <Button
          className='mt-4 zoomable'
          size='sm'
          variant='link'
          onClick={() => {
            navigate(`/play/${puzzleId}`);
          }}
        >
          Exit group
        </Button>
      </Modal.Body>
    </Modal>
  );







  // Start modal
  const [isCreatingGroupPlaySession, setIsCreatingGroupPlaySession] = useState(false);
  const [joinGroupPlaySessionInput, setJoinGroupPlaySessionInput] = useState(null);
  const [isCheckingGroupPlaySession, setIsCheckingGroupPlaySession] = useState(false);
  const [joinGroupPlaySessionError, setJoinGroupPlaySessionError] = useState(null);  // string
  async function navigateToNewPlaySession() { // parses the joinGroupPlaySessionInput and navigates to the groupPlaySession
    if (!joinGroupPlaySessionInput) return;
    // Confirm that it exists and is pointing to the same puzzleId
    try {
      setIsCheckingGroupPlaySession(true);
      const { puzzleId: groupPlayPuzzleId } = await (await get({
        apiName: 'groupPlaySessions',
        path: `/${joinGroupPlaySessionInput}`,
      }).response).body.json();
      if (puzzleId !== groupPlayPuzzleId) throw new Error('Wrong puzzle ID');
      navigate(`/group-play/${joinGroupPlaySessionInput}`);
    } catch (e) {
      console.log('Could not access group play session', e);
      setJoinGroupPlaySessionError(`Group ${joinGroupPlaySessionInput} does not exist for this puzzle!`)
    }
    setIsCheckingGroupPlaySession(false);
  }
  const startModal = (
    <Modal show={!showJoinGroupPlaySessionModal && playStatus === PlayStatus.START} fullscreen='sm-down' onHide={() => play()}>
      <Modal.Header className='teal-1-bkgd' closeButton>
        <strong>{groupPlaySessionId ? 'Group Play Session' : 'Start Puzzle'}</strong>
      </Modal.Header>
      <Modal.Body className='text-center'>
        <div className='fw-bold'>{title}</div>
        <div className='text-secondary small'>
          {author && <div>by {author}</div>}
          {date && <div>{date}</div>}
          {editor && <div>Edited by {editor}</div>}
          {publisher && <div>{publisher}</div>}
          {copyright && <div>&copy; {copyright}</div>}
        </div>
        <hr />
        {puzzleMetadata.get(PuzzleMetadataKey.START_MESSAGE) && <>
          <Badge className='mb-2' bg='success'>Constructor note</Badge>
          <p className='balanced-text'>{puzzleMetadata.get(PuzzleMetadataKey.START_MESSAGE)}</p>
          <hr />
        </>}
        
        {groupPlaySessionId && liveConnectionInfo ? (
          // in a fully-connected group play session
          <div className='d-flex flex-column gap-2 align-items-center'>

            <div className='text-primary'>
              <div>To invite friends, just share your page's URL.</div>
              <div className='text-xs text-secondary'>
                Players can still join after the game starts.
              </div>
            </div>
            <ShareLinkButton />

            <div className='mt-3 mb-5 d-flex align-items-center justify-content-center gap-2'>
              <div className='fw-bold'>joined:</div>
              <PlayToolbarAvatar
                color={myCursorColor}
                letter={displayNameInput.charAt(0)}
                tooltipText={displayNameInput + ' (me)'}
                size={30}
                backgroundOpacity={0.3}
              />
              {Object.values(liveConnectionInfo).map(({ displayName, cursorColor }, idx) => (
                <PlayToolbarAvatar
                  key={`connection-${idx}`}
                  color={cursorColor}
                  letter={displayName.charAt(0)}
                  tooltipText={displayName}
                  size={25}
                />
              ))}
            </div>

            <Button
              className='mt-2 px-5 zoomable-subtle d-flex align-items-center justify-content-center gap-2'
              variant='success'
              onClick={() => play()}
              size='lg'
            >
              Start game for everyone
              <FaRegArrowAltCircleRight />
            </Button>
          </div>
        ) : (<>
          <Button
            className='mt-2 px-5 fw-bold zoomable'
            variant='success'
            onClick={() => play()}
            size='lg'
          >
            <img className='me-2' src='/android-chrome-256x256.png' alt='Crossworthy' width='25px' />
            Play!
          </Button>

          <hr style={{ margin: '20px 90px 20px 90px' }} />

          <div className='fw-bold text-center'>or play together with friends:</div>
          <div className='mt-2 d-flex gap-2 align-items-center justify-content-center'>
          {isCreatingGroupPlaySession ? (
            <>
              <Spinner size='sm' variant='info' />
              <div className='text-info fst-italic'>Creating group play session...</div>
            </>
          ) : joinGroupPlaySessionInput !== null ? (
            <>
              <InputGroup>
                <InputGroup.Text className='text-nowrap'>crossworthy.net/group-play/</InputGroup.Text>
                <FormControl
                  placeholder='6-letter key'
                  autoFocus
                  disabled={isCheckingGroupPlaySession}
                  value={joinGroupPlaySessionInput}
                  onChange={e => setJoinGroupPlaySessionInput(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      navigateToNewPlaySession();
                    }
                  }}
                />
              </InputGroup>
              <LoaderButton
                className='py-2 d-flex align-items-center justify-content-center'
                variant='outline-success'
                size='sm'
                isLoading={isCheckingGroupPlaySession}
                onClick={() => navigateToNewPlaySession()}
              >
                <FaArrowRight />
              </LoaderButton>
              <Button
                className='py-2 d-flex align-items-center justify-content-center'
                variant='outline-danger'
                size='sm'
                onClick={() => {
                  setJoinGroupPlaySessionInput(null);
                }}
              >
                <HiOutlineX />
              </Button>
            </>
          ) : (
            <>
              <Button
                className='zoomable-subtle'
                variant='info'
                size='sm'
                onClick={async () => {
                  setIsCreatingGroupPlaySession(true);
                  const groupPlaySessionId = await createNewGroupPlaySession(puzzleId);
                  if (groupPlaySessionId) {
                    navigate('/group-play/' + groupPlaySessionId);
                  } else {
                    postErrorNotification('Error creating group play session', 'We are having trouble connecting right now. Please let us know if this continues!');
                  }
                  setIsCreatingGroupPlaySession(false);
                }}
              >Create a group session</Button>
              <Button
                className='zoomable-subtle'
                variant='info'
                size='sm'
                onClick={() => {
                  setJoinGroupPlaySessionInput('');
                }}
              >Join a group session</Button>
            </>
          )}
          </div>

          {joinGroupPlaySessionInput !== null && joinGroupPlaySessionError && (
            <div className='small text-center text-danger mt-1'>{joinGroupPlaySessionError}</div>
          )}

          <hr style={{ margin: '20px 90px 20px 90px' }} />

          <div className='mt-2 d-flex gap-1 align-items-center small justify-content-center'>
            <span>or download as:</span>
            <Button
              size='sm'
              variant='link'
              onClick={async () => {
                var doc;
                doc = await getPrintablePDF({ exportBoardForPng: undefined, puzzleMetadata, charGrid, furnishings, clues, options: {
                  includeAnswers: false,
                  includeClues: true,
                }});
                doc.save((puzzleMetadata.get(PuzzleMetadataKey.TITLE) || 'untitled puzzle') + '.pdf');
              }}
            >.pdf</Button>
            <Button
              size='sm'
              variant='link'
              onClick={() => {
                const puzBytes = getPuzFile({ puzzleMetadata, charGrid, clues });

                const a = window.document.createElement('a');
                a.href = window.URL.createObjectURL(new Blob([puzBytes], { type: 'application/octet-stream' }));
                a.download = `${puzzleMetadata.get(PuzzleMetadataKey.TITLE) || 'My Crossworthy Puzzle'}.puz`
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
            >.puz</Button>
          </div>
          <div className='align-items-center d-flex text-xs justify-content-center text-muted fst-italic'>
            (exports may not include formatting)
          </div>
        </>)}
      </Modal.Body>
    </Modal>
  );

  // Pause modal
  const pauseModal = (
    <Modal show={!showJoinGroupPlaySessionModal && playStatus === PlayStatus.PAUSE} fullscreen='sm-down' onHide={() => play()}>
      <Modal.Header className='teal-1-bkgd' closeButton>
        <strong>{groupPlaySessionId ? 'Group Play Session' : ''} Paused</strong>
      </Modal.Header>
      <Modal.Body className='text-center'>
        <div>Current time: {displayTime}</div>
        <hr />
        {groupPlaySessionId && liveConnectionInfo && (
          <div className='mt-3 mb-5 d-flex align-items-center justify-content-center gap-2'>
            <div className='fw-bold'>connected:</div>
            <PlayToolbarAvatar
              color={myCursorColor}
              letter={displayNameInput.charAt(0)}
              tooltipText={displayNameInput + ' (me)'}
              size={30}
              backgroundOpacity={0.3}
            />
            {Object.values(liveConnectionInfo).map(({ displayName, cursorColor }, idx) => (
              <PlayToolbarAvatar
                key={`connection-${idx}`}
                color={cursorColor}
                letter={displayName.charAt(0)}
                tooltipText={displayName}
                size={25}
              />
            ))}
          </div>
        )}
        <Button
          className='mt-2 px-3'
          variant='outline-success'
          onClick={() => play()}
        >Resume {groupPlaySessionId ? 'for everyone' : null}</Button>
        <div />
        <Button
          className='mt-3'
          variant='outline-danger'
          size='sm'
          onClick={() => {
            if (window.confirm('Reveal all the answers? This will end the game.')) {
              revealLocs(incorrectLocs.concat(emptyLocs));
            }
          }}
        >Reveal answers</Button>
        
        {!groupPlaySessionId && <div className='mt-2 d-flex gap-1 align-items-center small justify-content-center'>
          <span>download puzzle as:</span>
          <Button
            size='sm'
            variant='link'
            onClick={async () => {
              var doc;
              doc = await getPrintablePDF({ exportBoardForPng: undefined, puzzleMetadata, charGrid, furnishings, clues, options: {
                includeAnswers: false,
                includeClues: true,
              }});
              doc.save((puzzleMetadata.get(PuzzleMetadataKey.TITLE) || 'untitled puzzle') + '.pdf');
            }}
          >.pdf</Button>
          <Button
            size='sm'
            variant='link'
            onClick={() => {
              const puzBytes = getPuzFile({ puzzleMetadata, charGrid, clues });

              const a = window.document.createElement('a');
              a.href = window.URL.createObjectURL(new Blob([puzBytes], { type: 'application/octet-stream' }));
              a.download = `${puzzleMetadata.get(PuzzleMetadataKey.TITLE) || 'My Crossworthy Puzzle'}.puz`
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }}
          >.puz</Button>
        </div>}
      </Modal.Body>
    </Modal>
  );
  
  // Wrong modal
  const wrongModal = (
    <Modal show={showWrongModal} fullscreen='sm-down' onHide={() => {
      setShowWrongModal(false);
      setRevealNumberOfWrongLetters(false);
    }}>
      <Modal.Header className='teal-1-bkgd' closeButton>
        <strong>Almost there!</strong>
      </Modal.Header>
      <Modal.Body className='text-center'>
        <div>You've almost got it, but some letters are incorrect.</div>
        {revealNumberOfWrongLetters ? (
          <div className='text-info mt-3'>You have {possiblyPluralString(incorrectLocs.length, 'wrong letter')}.</div>
        ) : (
          <Button
            className='mt-3'
            variant='outline-info'
            size='sm'
            onClick={() => setRevealNumberOfWrongLetters(true)}
          >
            See number of wrong letters
          </Button>
        )}
      </Modal.Body>
    </Modal>
  );

  // End modal
  const [shareTextCopied, setShareTextCopied] = useState(false);
  const handleCopyShareText = () => {
    const shareable = document.getElementById('shareable');
    if (!shareable) return;

    // Collect all text content from child divs, separated by newlines
    const text = Array.from(shareable.querySelectorAll('div'))
      .map(div => div.innerText.trim())
      .join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setShareTextCopied(true);
    });
  };
  const endModal = (
    <Modal show={!showJoinGroupPlaySessionModal && showEndModal} fullscreen='sm-down' onHide={() => setShowEndModal(false)} onShow={() => setShowWrongModal(false)}>
      <Modal.Header className='teal-2-bkgd' closeButton>
        <strong>Bravo!</strong>
      </Modal.Header>
      <Modal.Body className='text-center'>
        <p className='h3'><strong>Solved in {displayTime} 🎉</strong></p>
        <p>Excellently done!</p>

        {puzzleMetadata.get(PuzzleMetadataKey.END_MESSAGE) && <>
          <hr className='mx-5' />
          <Badge className='mb-2' bg='success'>Constructor note</Badge>
          <p className='balanced-text'>{puzzleMetadata.get(PuzzleMetadataKey.END_MESSAGE)}</p>
        </>}

        <hr className='mx-5' />

        <div className='d-flex'>
          <div className='text-start small mx-auto rounded border py-2 px-2 zoomable-subtle clickable' onClick={handleCopyShareText}>
            <div id='shareable'>
              <div>I solved {author ? `${author}'s` : 'the'} crossword puzzle "{title || 'Crossworthy Puzzle'}" in {displayTime}!</div>
              <div>🔡 {clues.size} clues in a {charGrid.length}x{charGrid[0].length} grid{revealedLocs.length === 0 && ', with no reveals'}</div>
              <div>🧩 Solve here: <a href={`/play/${puzzleId}`} target='_blank' rel='noreferrer'>crossworthy.net/play/{puzzleId}</a></div>
            </div>

            <div className='w-100 d-flex justify-content-center mt-2'>
              <Button className='small px-2 py-0 mb-1 d-flex gap-2 align-items-center' variant={shareTextCopied ? 'success' : 'outline-success'} onClick={handleCopyShareText}>
                {shareTextCopied ? <FaCheck /> : <FaCopy />}{shareTextCopied ? 'Copied' : 'Copy'} to clipboard
              </Button>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );


  return (
    <>
      <div
        className={'w-100 d-flex mb-1 teal-1-bkgd ' + (brandIsShowing ? 'ms-3' : '')}
        style={{position: 'sticky', top: '0', paddingLeft: brandIsShowing ? '60px' : '6px', paddingRight: brandIsShowing ? '75px' : '50px', paddingTop: '3px', paddingBottom: '4px', zIndex: 100, boxShadow: '0 0 4px black'}}
      >
        <OverlayTrigger
          placement='bottom'
          trigger={['hover', 'focus', 'click']}
          rootClose
          overlay={
            <Tooltip>
              <strong>{title || 'Untitled puzzle'}</strong>{author && <em className='ms-2'>by {author}</em>}
            </Tooltip>
          }
        >
          <div className={'my-auto ' + (gridIsFullAndCorrect ? 'text-success' : 'text-dark')} style={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'}}>
            <span className='me-3' style={{fontSize: windowWidth > 767 ? '20px' : '15px'}}>{title || 'Untitled Puzzle'}</span>
            {author && <span style={{fontSize: windowWidth > 767 ? '14px' : '13px'}}>by {author}</span>}
          </div>
        </OverlayTrigger>
        <div className='ms-auto my-auto ps-2 text-end d-flex'>

          {liveConnectionInfo && (
            <div className='d-flex align-items-center gap-1 mx-3'>
              {windowWidth > 767 && <div className='fw-bold me-1 small'>players:</div>}
              <PlayToolbarAvatar
                color={myCursorColor}
                letter={displayNameInput.charAt(0)}
                tooltipText={displayNameInput + ' (me)'}
                size={25}
                backgroundOpacity={0.3}
              />
              {Object.values(liveConnectionInfo).map(({ displayName, cursorColor }, idx) => (
                <PlayToolbarAvatar
                  key={`connection-${idx}`}
                  color={cursorColor}
                  letter={displayName.charAt(0)}
                  tooltipText={displayName}
                  size={21}
                />
              ))}
            </div>
          )}

          <span className='small text-muted me-1 my-auto'>{playStatus === PlayStatus.END ? 'completed in ' : ''}{displayTime}</span>

          {playStatus === PlayStatus.END ? (
            !groupPlaySessionId && <Button
              className='p-1 mx-2 d-inline-flex'
              variant='outline-info'
              size='sm'
              onClick={() => {
                if (window.confirm('Restart this puzzle? This will remove all your progress.')) {
                  reset();
                }
              }}
            >
              <IoMdRefresh className='my-auto' />
              {brandIsShowing && <span className='ms-1'>Start over</span>}
            </Button>
          ) : (
            <>
              <OverlayTrigger
                placement='bottom'
                trigger={['hover', 'focus', 'click']}
                rootClose
                overlay={
                  <Tooltip>Add symbols, numbers, emojis, or multiple characters</Tooltip>
                }
              >
                <Button
                  className='p-1 ms-2 d-inline-flex'
                  variant='primary'
                  size='sm'
                  onClick={() => displayRebusDialog()}
                >
                  {brandIsShowing ? 'Rebus' : 'Reb.'}
                </Button>
              </OverlayTrigger>

              <Button
                className='p-1 d-inline-flex mx-2'
                variant='warning'
                size='sm'
                disabled={playStatus === PlayStatus.END}
                onClick={() => playStatus === PlayStatus.PLAY ? pause() : play()}
              >
                <IoPause className='my-auto' />
                {brandIsShowing && <span className='ms-1'>Pause</span>}
              </Button>

              <DropdownButton title={<span><FaVoteYea />{brandIsShowing && ' Reveal'}</span>} as={ButtonGroup} size='sm' variant='danger' disabled={playStatus === PlayStatus.END}>
                <Dropdown.Item
                  onClick={() => setPlayPreference(PlayPreferenceKey.SHOW_MISTAKES_INSTANTLY, !getPlayPreference(PlayPreferenceKey.SHOW_MISTAKES_INSTANTLY))}
                >Turn {getPlayPreference(PlayPreferenceKey.SHOW_MISTAKES_INSTANTLY) ? 'off' : 'on'} instant checking</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={() => {
                    if (window.confirm('Reveal all the answers? This will end the game.')) {
                      revealLocs(incorrectLocs.concat(emptyLocs));
                    }
                  }}
                >Reveal all answers</Dropdown.Item>
              </DropdownButton>
            </>
          )}
        </div>
      </div>

      {startModal}
      {pauseModal}
      {wrongModal}
      {endModal}
      
      {joinGroupPlaySessionModal}
    </>
  );

}